"use client";
import CommonAuth from "@/components/auth/CommonAuth";
import Marquee from "react-fast-marquee";
import {
  BlueHeart,
  Close,
} from "@/components/common/Icons";
import { ImageLogo } from "@/components/common/ImageLogo";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useAuth } from "@/hook/useAuth";
import {
  addFavoritesCards,
  getFavoritesCards,
  removeFavoritesCards,
} from "@/store/feature/ai-course-finder/aiCourseApi";
import { setApplicationRequirementsOpen } from "@/store/feature/common/commonSlice";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { CourseItem } from "@/store/types/courseTypes";
import { checkCountry, replacedName } from "@/utils/Constants";
import { getFormattedCountryName } from "@/utils/getFormattedCountryName";
import { Check, Plus } from "lucide-react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useCallback, useEffect, useState, useMemo } from "react";
import ReactDOM from "react-dom";
import { getCourseIntakeInfo } from "@/utils/courseIntakeHelper";
import CheckEligibilityModal from "@/components/common/ai-course-finder/CheckEligibilityModal";
import ApplicationRequirementsPopUp from "@/components/common/ApplicationRequirementsPopUp";
import EligibilityCriteriaPopUp from "@/components/common/EligibilityCriteriaPopUp";
import EmailVerification from "@/components/common/EmailVerificationPopup";
import PhoneVerification from "@/components/common/PhoneVerification";
import { useFullPath } from "@/hook/useFullPath";

interface CoursesCardsProps {
  coursesCards: CourseItem;
  setComparisonData?: React.Dispatch<React.SetStateAction<CourseItem[]>>;
  comparisonData?: CourseItem[];
  showFavorite?: boolean;
  isAiCourseFinder?: boolean;
  aiSourceSlug?: string;
  onAskAboutThis?: (course: CourseItem) => void;
  index?: number;
}

const UniversityCourseCard = ({
  coursesCards: items, // Rename internally to items to easily reuse NewCoursesCards logic
  setComparisonData,
  comparisonData,
  showFavorite = true,
  isAiCourseFinder = false,
  aiSourceSlug = "",
  onAskAboutThis,
  index = 1,
}: CoursesCardsProps) => {
  const dispatch = useAppDispatch();
  const pastUrl = useFullPath();
  const router = useRouter();

  // Logic from NewCoursesCards
  const { favoritesCardList } = useAppSelector((state) => state.aiCourseFinder);
  const favoritesCardId = Array.isArray(favoritesCardList?.courses)
    ? favoritesCardList.courses.map((item) => item.course_id)
    : [];

  const { isAnonymous, user, loading: authLoading } = useAuth();
  const { userDetails, appliedApplications } = useAppSelector(
    (state) => state.userProfile,
  );

  const [isOpen, setIsOpen] = useState(false); // Modal state
  const [isEligibilityChecked, setIsEligibilityChecked] = useState<boolean>(false);
  const { appliedCourseData, academicDetails, englishProficiencyDetails } = useAppSelector((state) => state.course);

  const [registrationLoginPopup, setRegistrationLoginPopup] = useState<boolean>(false);
  const [emailVerificationPopUp, setEmailVerificationPopup] = useState<boolean>(false);
  const [phoneVerificationPopUp, setPhoneVerificationPopUp] = useState<boolean>(false);
  const [eligibilityCriteriaPopUp, setEligibilityCriteriaPopUp] = useState<boolean>(false);
  const [loading, setLoading] = useState(false);
  const [applicationRequirementPopUp, setApplicationRequirementPopUp] = useState<boolean>(false);
  const [isAlreadyApplied, setIsAlreadyApplied] = useState(false);
  const [hasMaxAppliedLimit, setHasMaxAppliedLimit] = useState(false);
  const [hasReachedUniLimit, setHasReachedUniLimit] = useState(false);
  const [hasReachedCountryLimit, setHasReachedCountryLimit] = useState(false);
  const [favoritePopUp, setFavoritePopUp] = useState(false);
  const [isNavigating, setIsNavigating] = useState(false);
  const [pendingApplyAfterAuth, setPendingApplyAfterAuth] = useState(false);
  const [hasPassedEligibilityCheck, setHasPassedEligibilityCheck] = useState(false);

  const hasUserEmail = Boolean(user?.email?.trim() || userDetails?.email?.trim());

  const handleRedirect = () => {
    if (isNavigating) return;
    setIsNavigating(true);
    router.push(`/${items.course_slug}/course-details`);
  };

  useEffect(() => {
    if (!items || !Array.isArray(appliedCourseData) || !Array.isArray(appliedApplications)) return;
    if (appliedApplications.length >= 5) {
      setHasMaxAppliedLimit(true);
    }
    const countryData = appliedCourseData.find(
      (item) => item?.countryName?.toLowerCase?.() === (items?.address_info?.country || items?.country)?.toLowerCase?.(),
    );
    if (!countryData) return;
    if (items.id && countryData.courseIds?.includes(items.id as string)) {
      setIsAlreadyApplied(true);
    }
    const collegeApplications = countryData.collegeNames?.filter(
      (name) => name === (items.universitiesName || items.college_name),
    ) ?? [];
    setHasReachedUniLimit(collegeApplications.length >= (countryData.courseLimitPerUni || 0));
    setHasReachedCountryLimit(countryData.collegeNames?.length >= countryData.maxUnisPerCountry);
  }, [items, appliedCourseData, appliedApplications]);

  const hasAnyValue = (obj: Record<string, any>) => {
    return obj && Object.values(obj).some((value) => value !== "");
  };

  useEffect(() => {
    if (isAiCourseFinder && aiSourceSlug === "eligibility") {
      setIsEligibilityChecked(true);
    } else {
      if (hasAnyValue(academicDetails) && hasAnyValue(englishProficiencyDetails)) {
        setIsEligibilityChecked(true);
      } else {
        setIsEligibilityChecked(false);
      }
    }
  }, [academicDetails, englishProficiencyDetails, isAiCourseFinder, aiSourceSlug]);

  const [isChecked, setIsChecked] = useState(false);

  useEffect(() => {
    if (comparisonData && comparisonData.some((item) => item.course_id === items.course_id || item.id === items.id)) {
      setIsChecked(true);
    } else {
      setIsChecked(false);
    }
  }, [comparisonData, items.course_id, items.id]);

  const handleComparisonChange = () => {
    setIsChecked(!isChecked);
    if (setComparisonData) {
      setComparisonData((prev) => {
        if (!prev) return [];
        if (!isChecked) {
          if (prev.length >= 10) return prev;
          return [...prev, items];
        } else {
          return prev.filter((i) => i.id !== items.id && i.course_id !== items.course_id);
        }
      });
    }
  };

  useEffect(() => {
    if (registrationLoginPopup) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [registrationLoginPopup]);

  const continueApplyFlow = useCallback(() => {
    if (!hasUserEmail && isAiCourseFinder) {
      setEmailVerificationPopup(true);
      return;
    }
    if (userDetails?.primary_verified === false && isAiCourseFinder) {
      setPhoneVerificationPopUp(true);
      return;
    }
    if (isAiCourseFinder && aiSourceSlug === "eligibility") {
      setApplicationRequirementPopUp(true);
      dispatch(setApplicationRequirementsOpen(true));
      return;
    }
    if (hasPassedEligibilityCheck) {
      setApplicationRequirementPopUp(true);
      if (isAiCourseFinder) {
        dispatch(setApplicationRequirementsOpen(true));
      }
      return;
    }
    if (isEligibilityChecked) {
      setEligibilityCriteriaPopUp(true);
    } else {
      setIsOpen(true);
    }
  }, [dispatch, hasUserEmail, isAiCourseFinder, aiSourceSlug, isEligibilityChecked, hasPassedEligibilityCheck, userDetails?.primary_verified]);

  useEffect(() => {
    if (emailVerificationPopUp && hasUserEmail) {
      setEmailVerificationPopup(false);
    }
  }, [emailVerificationPopUp, hasUserEmail]);

  const buttonHandler = (e?: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e?.preventDefault();
    e?.stopPropagation();
    localStorage.setItem("appliedUrl", pastUrl);

    if (isAnonymous) {
      if (authLoading) return;
      setPendingApplyAfterAuth(true);
      setRegistrationLoginPopup(true);
      return;
    }
    continueApplyFlow();
  };

  useEffect(() => {
    if (!pendingApplyAfterAuth || isAnonymous || authLoading) return;
    setRegistrationLoginPopup(false);
    continueApplyFlow();
    setPendingApplyAfterAuth(false);
  }, [continueApplyFlow, isAnonymous, pendingApplyAfterAuth, authLoading]);

  const getButtonConfig = () => {
    if (isAlreadyApplied) {
      return { text: "Already Applied", tooltip: "You have already applied to this course" };
    } else if (hasMaxAppliedLimit) {
      return { text: "Application Limit Reached", tooltip: "You've reached the maximum number of applications allowed" };
    } else if (hasReachedUniLimit) {
      return { text: "University Limit Reached", tooltip: "You've reached the maximum applications for this university" };
    } else if (hasReachedCountryLimit) {
      return { text: "Country Limit Reached", tooltip: "You've reached the maximum applications for this country" };
    } else if (isEligibilityChecked && isAiCourseFinder && aiSourceSlug === "eligibility") {
      return { text: "Apply via Edvia", tooltip: "" };
    } else {
      return { text: "Check Eligibility", tooltip: "" };
    }
  };

  const isButtonDisabled = isAlreadyApplied || hasReachedUniLimit || hasReachedCountryLimit || hasMaxAppliedLimit;
  const buttonConfig = getButtonConfig();

  const isFavoriteLocal = !!items.course_id && favoritesCardId.includes(items.course_id);

  const addFavorites = async () => {
    if (!isAnonymous) {
      try {
        setLoading(true);
        const payload = {
          favouriteId: items.course_id || "",
          studentId: user?.uid || "",
          type: "course",
        };
        await dispatch(addFavoritesCards(payload));
        await dispatch(getFavoritesCards(payload.studentId));
        setLoading(false);
      } catch (error) {
        console.error("error", error);
        setLoading(false);
      }
    } else {
      if (authLoading) return;
      setRegistrationLoginPopup(true);
      setLoading(false);
    }
  };

  const confirmRemove = async () => {
    setFavoritePopUp(true);
  };

  const removeFavorites = async () => {
    if (loading) return;
    setLoading(true);
    const payload = {
      favouriteId: items.course_id || "",
      userId: user?.uid || "",
    };
    await dispatch(removeFavoritesCards(payload));
    await dispatch(getFavoritesCards(payload.userId));
    setFavoritePopUp(false);
    setLoading(false);
  };

  console.log("Full API Response for Course:", items);

  const scholarshipSrc = items?.scholarshipDetails || items?.scholarship_details;
  const highest = scholarshipSrc
    ? Object.entries(scholarshipSrc).reduce(
      (maxItem, [key, value]: any) => {
        const currentMax = Number(value?.max || value?.["Max Amount"] || 0);
        return currentMax > maxItem.max
          ? { type: key, max: currentMax }
          : maxItem;
      },
      { type: "", max: 0 },
    )
    : { type: "", max: 0 };

  const intakeInfo = useMemo(() => {
    return getCourseIntakeInfo(items.intake_details, (items as any)?.intake_priority);
  }, [items.intake_details, (items as any)?.intake_priority]);

  const rawStatus = items.intakeStatus || (items as any)?.intake_status;
  const isUpcoming =
    rawStatus?.toLowerCase() === "upcoming" ||
    (!intakeInfo.isOpen &&
      Array.isArray(items.intake_details?.status) &&
      items.intake_details.status.length > 0);

  const isApplyButtonDisabled = isButtonDisabled || isUpcoming;

  const rawPermit = items?.post_course_work_permit;
  const hasPermit = rawPermit && String(rawPermit).toLowerCase() !== "false" && String(rawPermit).toLowerCase() !== "no";
  const permitText = items?.post_course_work_duration
    ? `${items.post_course_work_duration}`
    : typeof rawPermit === "string" && rawPermit !== "true" && rawPermit !== "yes"
      ? `${rawPermit}`
      : "Yes";

  const rawWaiver = items?.englishWaiverAccepted ?? items?.english_waiver_accepted;
  const hasWaiver = rawWaiver === true || String(rawWaiver).toLowerCase() === "true" || String(rawWaiver).toLowerCase() === "yes";

  return (
    <>
      <div className={`shadow-[0px_4px_12px_0px_#00000040] rounded-[12px] py-5 px-3 lg:px-4 lg:py-8 h-full relative bg-white`}>
        <div className="flex flex-col h-full justify-between">
          <div>
            <div className="relative p-4 sm:p-[17px] bg-gradient-to-r from-[#2962FF] to-[#8A2BE2] flex gap-3 sm:gap-4 min-h-[130px] sm:min-h-[140px] items-start rounded-t-[12px] -mt-5 -mx-3 lg:-mt-8 lg:-mx-4 mb-4">
              {/* Logo */}
              <div className="rounded-xl overflow-hidden min-w-[60px] sm:min-w-[70px] max-w-[60px] sm:max-w-[70px] h-[60px] sm:h-[70px] bg-white flex items-center justify-center shadow-sm">
                <Link
                  href={`/${items.collegeSlug || items.universitiesName?.toLowerCase().replace(/\s+/g, '-')}`}
                  className="w-full h-full p-1 cursor-pointer flex items-center justify-center"
                >
                  <ImageLogo
                    college_name={(items.universitiesName || items.college_name) ?? ""}
                    country={items.address_info?.country ?? items.country ?? ""}
                  />
                </Link>
              </div>

              <div className="w-full pr-8">
                <Link href={`/course-details/${items.course_slug}`} className="cursor-pointer block">
                  <p className="ff_poppins font-bold text-sm sm:text-base md:text-lg text-white mb-1 leading-tight line-clamp-2">
                    {items.courseName || items.course_details?.course_name}{" "}
                    {items.course_details?.course_specialisation && (
                      <span className="font-normal italic text-xs sm:text-sm md:text-base">
                        {`(${items.course_details?.course_specialisation})`}
                      </span>
                    )}
                  </p>
                </Link>

                <Link
                  href={`/${items.collegeSlug || items.universitiesName?.toLowerCase().replace(/\s+/g, '-')}`}
                  className="cursor-pointer flex items-center gap-2 mb-1"
                >
                  <p className="ff_poppins font-bold text-[16px] sm:text-[18px] text-white/90 leading-tight">
                    {items?.universitiesName || items?.college_name}
                  </p>
                  {items?.category && (
                    <span className="bg-white/20 backdrop-blur-md border border-white/30 text-white shadow-sm font-medium text-[10px] sm:text-xs px-2 py-0.5 rounded-full whitespace-nowrap capitalize">
                      {items.category}
                    </span>
                  )}
                </Link>

                {(() => {
                  if (!items?.campusDetails || typeof items.campusDetails !== "object") {
                    return (
                      <p className="ff_poppins font-normal text-[16px] text-white/80 leading-tight line-clamp-1 capitalize">
                        {(items?.address_info?.country || items?.country || "").replaceAll("europe - ", "").trim() || "N/A"}
                      </p>
                    );
                  }
                  const campusObjValues = Array.isArray(items.campusDetails)
                    ? items.campusDetails
                    : Object.values(items.campusDetails);
                  const campusList: string[] = [];
                  campusObjValues.forEach((c: any) => {
                    if (!c) return;
                    const city = c?.city || "";
                    if (city && !campusList.includes(city)) campusList.push(city);
                  });
                  if (campusList.length === 0) {
                    return (
                      <p className="ff_poppins font-normal text-xs text-white/80 leading-tight line-clamp-1">
                        {(items?.address_info?.country || items?.country || "").replaceAll("europe - ", "").trim() || "N/A"}
                      </p>
                    );
                  }
                  if (campusList.length > 1) {
                    return (
                      <div className="w-full max-w-full overflow-hidden text-xs text-white/80 leading-tight">
                        <Marquee gradient={false} speed={25} pauseOnHover>
                          {campusList.map((c, idx) => (
                            <span key={idx} className="mr-3 inline-flex items-center gap-1 font-normal text-xs text-white/90">
                              {c}
                              {idx < campusList.length - 1 && <span className="ml-2 text-white/50">•</span>}
                            </span>
                          ))}
                        </Marquee>
                      </div>
                    );
                  }
                  return (
                    <p className="ff_poppins font-normal text-xs text-white/80 leading-tight line-clamp-1">
                      {campusList[0]}
                      {items?.country ? ` • ${items.country}` : ""}
                    </p>
                  );
                })()}
              </div>

              {/* Heart Icon Top Right */}
              {showFavorite && (
                <span
                  onClick={() => {
                    if (!loading) {
                      if (isFavoriteLocal) {
                        confirmRemove();
                      } else {
                        addFavorites();
                      }
                    }
                  }}
                  className={`absolute top-4 right-4 size-[32px] inline-flex bg-white/20 backdrop-blur-sm rounded-full items-center justify-center ${loading ? "cursor-disabled opacity-50" : "cursor-pointer hover:bg-white/30 transition"
                    }`}
                >
                  <BlueHeart isFavorite={isFavoriteLocal} />
                </span>
              )}

              {/* Popular Pill Bottom Right */}
              {items.popularity === "High" && (
                <div className="absolute bottom-[-12px] right-4 bg-white text-[#2962FF] px-3 py-1 rounded-full text-xs font-bold shadow-md z-10 border border-[#2962FF]/10">
                  Popular
                </div>
              )}
            </div>

            {/* Intake Status Badge & Deadline */}
            <div className="ff_poppins mt-2.5 flex items-center gap-2 flex-wrap">
              {items?.course_details?.course_level && (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-[#F0F5FF] text-[#2962FF] border border-[#2962FF]/20">
                  {items.course_details.course_level}
                </span>
              )}
              {intakeInfo.isOpen ? (
                <>
                  <span
                    className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${intakeInfo.badgeVariant === "closing-soon"
                      ? "bg-[#FFF4E5] text-[#D97706]"
                      : "bg-[#E6F4ED] text-[#0F9755]"
                      }`}
                  >
                    <span
                      className={`size-2 rounded-full ${intakeInfo.badgeVariant === "closing-soon"
                        ? "bg-[#D97706] animate-pulse"
                        : "bg-[#0F9755]"
                        }`}
                    />
                    {intakeInfo.badgeLabel}
                  </span>

                  {intakeInfo.closingSoonText && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-bold bg-[#FEF3C7] text-[#92400E] border border-[#FDE68A]">
                      ⏳ {intakeInfo.closingSoonText}
                    </span>
                  )}

                  <span className={`text-xs sm:text-sm font-medium text-[#667085]`}>
                    {intakeInfo.intakeText}
                    {intakeInfo.deadlineText ? ` • ${intakeInfo.deadlineText}` : ""}
                  </span>
                </>
              ) : (
                <>
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-[#FFEBEB] text-[#D92D20]">
                    <span className="size-2 rounded-full bg-[#D92D20]" />
                    Closed
                  </span>
                  <span className={`text-xs sm:text-sm font-medium text-[#667085]`}>
                    {intakeInfo.intakeText}
                    {intakeInfo.deadlineText ? ` • ${intakeInfo.deadlineText}` : ""}
                  </span>
                </>
              )}
            </div>

            <div className="grid grid-cols-3 mt-4 gap-x-2">
              {items.tuitionFees ? (
                <div className={`rounded-[8px] p-[9px] lg:p-[10px] bg-[#D4E6FF]`}>
                  <p className={`ff_poppins text-[10px] lg:text-xs font-medium text-center text-primary`}>
                    Annual Fees
                  </p>
                  <p className={`text-xs lg:text-sm font-semibold ff_poppins text-center text-black!`}>
                    {items.currency} {items.tuitionFees}
                  </p>
                </div>
              ) : null}
              {items.jobRate && (
                <div className={`rounded-[8px] p-[10px] bg-[#D4E6FF]`}>
                  <p className={`ff_poppins text-[10px] lg:text-xs font-medium text-center text-primary`}>
                    Graduate employment rate
                  </p>
                  <p className={`text-xs lg:text-sm font-semibold ff_poppins text-center text-black!`}>
                    {items.jobRate}
                  </p>
                </div>
              )}
              {items.roiYearToPay && (
                <div className={`rounded-[8px] p-[10px] bg-[#D4E6FF]`}>
                  <p className={`ff_poppins text-[10px] lg:text-xs font-medium text-center text-primary`}>
                    ROI
                  </p>
                  <p className={`text-xs lg:text-sm font-semibold ff_poppins text-center text-black!`}>
                    {items.roiYearToPay} yrs
                  </p>
                </div>
              )}
            </div>

            <div className="grid grid-cols-3 mt-4 gap-2">
              {items.duration ? (
                <div className={`rounded-[8px] p-[9px] lg:p-[10px] bg-[#D4E6FF]`}>
                  <p className={`ff_poppins text-[10px] lg:text-xs font-medium text-center text-primary`}>
                    Duration
                  </p>
                  <p className={`text-xs lg:text-sm font-semibold ff_poppins text-center text-black!`}>
                    {items.duration}
                  </p>
                </div>
              ) : null}
              {Number(highest.max) > 0 && (
                <div className={`rounded-[8px] p-[10px] bg-[#D4E6FF]`}>
                  <p className={`ff_poppins text-[10px] lg:text-xs font-medium text-center text-primary`}>
                    Scholarship
                  </p>
                  <p className={`text-xs lg:text-sm font-semibold ff_poppins text-center text-black!`}>
                    {highest.max} {items.currency || items.fee_details?.tuition_fees_currency || "GBP"}
                  </p>
                </div>
              )}
              {hasPermit && (
                <div className={`rounded-[8px] p-[10px] bg-[#D4E6FF]`}>
                  <p className={`ff_poppins text-[10px] lg:text-xs font-medium text-center text-primary`}>
                    Post Work Permit
                  </p>
                  <p className={`text-xs lg:text-sm font-semibold ff_poppins text-center text-black!`}>
                    {permitText}
                  </p>
                </div>
              )}

            </div>

            <div className="mt-4 lg:mt-5 flex items-center justify-start flex-wrap gap-1">
              {/* {items.duration && (
                <p className={`text-[10px] lg:text-xs ff_poppins font-medium py-1 px-2 rounded-full bg-[#E5F0FF] text-black`}>
                  {items.duration}
                </p>
              )}
              {Number(highest.max) > 0 && (
                <p className={`text-[10px] lg:text-xs ff_poppins font-medium py-1 px-2 rounded-full bg-[#E5F0FF] text-black`}>
                  Scholarship {highest.max} {items.currency || items.fee_details?.tuition_fees_currency || "GBP"}
                </p>
              )} */}
              {/* {(() => {
                const waiver = items?.englishWaiverAccepted ?? items?.english_waiver_accepted;
                return (waiver === true || String(waiver).toLowerCase() === "true" || String(waiver).toLowerCase() === "yes") && (
                  <p className={`text-[10px] lg:text-xs ff_poppins font-medium py-1 px-2 rounded-full bg-[#E5F0FF] text-black`}>
                    English Waiver
                  </p>
                );
              })()} */}
              {/* {(() => {
                const permit = items?.post_course_work_permit;
                if (!permit || String(permit).toLowerCase() === "false" || String(permit).toLowerCase() === "no") return null;
                const permitText = items?.post_course_work_duration
                  ? `PSW: ${items.post_course_work_duration}`
                  : typeof permit === "string" && permit !== "true" && permit !== "yes"
                    ? `PSW: ${permit}`
                    : "Post Course Work Permit";
                return (
                  <p className={`text-[10px] lg:text-xs ff_poppins font-medium py-1 px-2 rounded-full bg-[#E5F0FF] text-black`}>
                    {permitText}
                  </p>
                );
              })()} */}
            </div>
          </div>
          <div>
            <div className="border-t border-t-[#727272] mt-4 flex items-center gap-3 pt-2 lg:pt-3">
              <div
                onClick={() => {
                  if (onAskAboutThis) {
                    onAskAboutThis(items);
                  } else {
                    handleRedirect();
                  }
                }}
                className={`w-1/2 rounded-xl lg:rounded-[16px] border border-gray-300 relative cursor-pointer hover:border-[#3776FF] hover:text-[#3776FF] hover:bg-blue-50 transition-colors shadow-sm`}
              >
                <div className={`relative z-10 p-[10px] flex items-center gap-2 justify-center transition-opacity text-[#333] hover:text-[#3776FF]`}>
                  <p className="text-xs lg:text-sm font-medium ff_poppins text-center">
                    View Detail
                  </p>
                </div>
              </div>
              <div className="flex flex-col items-center w-1/2">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="inline-block w-full">
                        <button
                          type="button"
                          className={`font-semibold ff_poppins px-3 lg:px-4 text-white text-xs lg:text-sm p-[10px] lg:p-[12px] rounded-xl lg:rounded-[16px] gradient_button_shadow bg-primary-gradient text-center ease-linear duration-300 w-full ${isUpcoming || isButtonDisabled ? "opacity-60 cursor-not-allowed" : "cursor-pointer hover:scale-105"}`}
                          onMouseDown={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                          }}
                          onClick={(e) => {
                            if (!isApplyButtonDisabled) {
                              buttonHandler(e as any);
                            }
                          }}
                          disabled={isApplyButtonDisabled}
                        >
                          {buttonConfig.text}
                        </button>
                      </span>
                    </TooltipTrigger>
                    {!isUpcoming && buttonConfig.tooltip && (
                      <TooltipContent className="bg-white shadow">
                        <p className="ff_poppins w-[250px] flex flex-wrap cursor-pointer font-semibold text-lg text-black leading-[27px] mb-[2px] line-clamp-1">
                          {buttonConfig.tooltip}
                        </p>
                      </TooltipContent>
                    )}
                  </Tooltip>
                </TooltipProvider>
              </div>
            </div>
            <div className={`flex items-center justify-center mt-3 gap-1.5 text-black`}>
              <div
                onClick={handleComparisonChange}
                className="flex items-center justify-center gap-1.5 cursor-pointer"
              >
                {!isChecked ? <Plus /> : <Check />}
                <p className="text-xs lg:text-sm font-medium ff_poppins">
                  {!isChecked ? "Add to compare" : "Shortlisted"}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>


      {/* Popups & Modals */}
      {registrationLoginPopup && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[100] flex items-center justify-center">
          <div className="bg-white w-full !max-w-[360px] max-h-[90vh] sm:!max-w-[450px] lg:!max-w-[500px] xl:!max-w-[570px] px-5 sm:px-7 md:px-8 lg:px-10 3xl:px-14 rounded-xl overflow-y-auto custom-scrollbar pb-5 sm:pb-7 md:pb-8 lg:pb-10 3xl:pb-14 pt-3 sm:pt-5 lg:pt-7 3xl:pt-7 relative mx-3">
            <span
              onClick={() => {
                setRegistrationLoginPopup(false);
                setPendingApplyAfterAuth(false);
              }}
              className="size-4 md:size-5 xl:size-6 absolute top-4 md:top-5 right-4 md:right-5 xl:top-7 xl:right-7 cursor-pointer z-10"
            >
              <Close />
            </span>
            <CommonAuth setShow={setRegistrationLoginPopup} />
          </div>
        </div>
      )}

      {emailVerificationPopUp && (
        <div
          className="fixed bottom-0 left-0 w-full h-full z-[999] flex items-end justify-center"
          onClick={() => setEmailVerificationPopup(false)}
        >
          <span className="inset-0 bg-black/60 backdrop-blur-[6px] absolute top-0 left-0 w-full h-full cursor-pointer" />
          <div
            className="relative z-20 w-full max-w-md max-h-[90vh] overflow-y-auto rounded-t-[32px] bg-[#0E1116] shadow-[0px_-4px_12px_0px_#ffffff40] p-4"
            onClick={(e) => e.stopPropagation()}
          >
            <EmailVerification setEmailVerificationPopup={setEmailVerificationPopup} />
          </div>
        </div>
      )}

      {phoneVerificationPopUp && (
        <PhoneVerification setPhoneVerificationPopup={setPhoneVerificationPopUp} />
      )}

      {isOpen && (
        <div className="fixed bottom-0 left-0 w-full h-full z-[999] flex items-end justify-center">
          <span
            className="inset-0 bg-black/60 backdrop-blur-[6px] absolute top-0 left-0 w-full h-full cursor-pointer"
            onClick={() => setIsOpen(false)}
          />
          <CheckEligibilityModal
            isOpen={isOpen}
            setIsOpen={setIsOpen}
            country={items?.address_info?.country ?? items?.country ?? ""}
            setEligibilityCriteriaPopUp={setEligibilityCriteriaPopUp}
          />
        </div>
      )}

      {eligibilityCriteriaPopUp && (
        <EligibilityCriteriaPopUp
          selectedCourse={items}
          setIsOpen={setIsOpen}
          setEligibilityCriteriaPopUp={setEligibilityCriteriaPopUp}
          setApplicationRequirementPopUp={(val) => {
            setApplicationRequirementPopUp(val);
            if (val) {
              setHasPassedEligibilityCheck(true);
            }
          }}
          isAiCourseFinder={isAiCourseFinder}
        />
      )}

      {applicationRequirementPopUp && (
        <ApplicationRequirementsPopUp
          selectedCourse={items}
          setApplicationRequirementPopUp={setApplicationRequirementPopUp}
          isAiCourseFinder={isAiCourseFinder}
          setIsOpen={setIsOpen}
        />
      )}

      {favoritePopUp &&
        typeof window !== "undefined" &&
        ReactDOM.createPortal(
          <div
            className="fixed bottom-0 left-0 w-full h-full z-[999] flex items-end justify-center"
            onClick={() => {
              if (!loading) setFavoritePopUp(false);
            }}
          >
            <span className="inset-0 bg-black/60 backdrop-blur-[6px] absolute top-0 left-0 w-full h-full cursor-pointer" />
            <div
              className="bg-[#0E1116] text-white relative z-20 max-w-md w-full rounded-t-[32px] text-center shadow-[0px_-4px_12px_0px_#ffffff40] overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <p className="text-base sm:text-lg md:text-xl font-semibold bg-white/10 py-6 px-4">
                <span className="max-w-[408px] inline-block">
                  Are you sure you want to remove this from favourites?
                </span>
              </p>

              <div className="grid grid-cols-2 gap-3 mt-5 pb-8 px-4 sm:px-6">
                <button
                  type="button"
                  onClick={() => {
                    removeFavorites();
                  }}
                  disabled={loading}
                  className="text-white py-3 sm:py-4 px-4 rounded-full border border-[#0162E8] font-medium text-base sm:text-[18px] bg-[#0162E8] hover:bg-[#0150c7] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? "Removing..." : "Yes"}
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setFavoritePopUp(false);
                  }}
                  disabled={loading}
                  className="text-white py-3 sm:py-4 px-4 rounded-full border border-white/20 text-base sm:text-[18px] font-medium hover:bg-white/10 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  No
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
};

export default UniversityCourseCard;
